import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Loader2, Check } from "lucide-react";

/**
 * DigestSettingsCard — Tier 2 counsellor tool.
 *
 * Renders a compact settings card that lets the counsellor:
 *   1. See a live count of deadlines that would land in Monday's email
 *   2. Toggle the weekly digest on/off
 *   3. Send themselves an instant preview
 *
 * Mounted once at the top of the CounsellorsDashboard cohort list
 * view (outside individual cohort scope, since the digest spans
 * every cohort the counsellor owns).
 */
export default function DigestSettingsCard({ me, onMeChanged }) {
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const optedIn = !!me?.email_digest_opt_in;

  useEffect(() => {
    let cancelled = false;
    api.get("/counsellors/digest/preview")
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const next = !optedIn;
      const r = await api.patch("/counsellors/me/digest", { email_digest_opt_in: next });
      onMeChanged?.({ ...me, email_digest_opt_in: r.data.email_digest_opt_in });
      toast.success(next
        ? "Weekly digest on — you'll get an email every Monday morning"
        : "Weekly digest off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update preference");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/counsellors/digest/send-now");
      toast.success(`Digest emailed to ${r.data.to} · ${r.data.total} deadlines`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — try again");
    } finally {
      setSending(false);
    }
  };

  const total = preview?.total ?? 0;
  const window = preview?.window_days ?? 14;

  return (
    <section
      className="border border-stone-200 rounded-lg bg-white p-4 flex items-center gap-4 flex-wrap"
      data-testid="digest-settings-card"
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Mail className="w-4 h-4 text-[#4C1D95] shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-stone-900" data-testid="digest-total">
            {preview
              ? `${total} deadline${total === 1 ? "" : "s"} in the next ${window} days`
              : "Loading digest…"}
          </div>
          <div className="text-xs text-stone-500">
            Weekly summary across every student in every cohort you own.
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer"
               data-testid="digest-optin-label">
          <span className="text-stone-600">Send Monday digest</span>
          <button
            type="button"
            role="switch"
            aria-checked={optedIn}
            onClick={toggle}
            disabled={saving}
            data-testid="digest-optin-toggle"
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              optedIn ? "bg-[#4C1D95]" : "bg-stone-300"
            } ${saving ? "opacity-60" : ""}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                optedIn ? "translate-x-4" : "translate-x-0.5"
              }`}
            />
          </button>
        </label>

        <button
          type="button"
          onClick={sendNow}
          disabled={sending || total === 0}
          data-testid="digest-send-now"
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-[#4C1D95] text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
          title={total === 0 ? "Nothing to send this week" : "Email a preview to yourself now"}
        >
          {sending
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Check className="w-3.5 h-3.5" />}
          Send preview
        </button>
      </div>
    </section>
  );
}
